import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { playSidebarSlide } from "@/shared/components/layout/sidebar-slide";
import { useReducedMotion } from "@/shared/hooks/use-reduced-motion";

type Scope = "workspace" | "payroll";
interface Props {
  scope: Scope;
  workspace: ReactNode;
  payroll: ReactNode;
}

/** Keeps just the departing panel alive while the new route is already usable. */
export function SidebarNavTransition({ scope, workspace, payroll }: Props) {
  const reducedMotion = useReducedMotion();
  const root = useRef<HTMLDivElement>(null);
  const workspacePanel = useRef<HTMLDivElement>(null);
  const payrollPanel = useRef<HTMLDivElement>(null);
  const focusWasInside = useRef(false);
  const lastScope = useRef(scope);
  const [presence, setPresence] = useState<{ current: Scope; outgoing: Scope | null }>({
    current: scope,
    outgoing: null,
  });
  // This is transient animation presence, not a second source of route state.
  if (presence.current !== scope) {
    setPresence({ current: scope, outgoing: reducedMotion ? null : presence.current });
  }

  useEffect(() => {
    function rememberFocus(event: FocusEvent | PointerEvent) {
      focusWasInside.current =
        event.target instanceof Node && Boolean(root.current?.contains(event.target));
    }
    document.addEventListener("focusin", rememberFocus);
    document.addEventListener("pointerdown", rememberFocus, true);
    return () => {
      document.removeEventListener("focusin", rememberFocus);
      document.removeEventListener("pointerdown", rememberFocus, true);
    };
  }, []);

  useLayoutEffect(() => {
    const changed = lastScope.current !== scope;
    lastScope.current = scope;
    const incoming = scope === "payroll" ? payrollPanel.current : workspacePanel.current;
    const outgoing = scope === "payroll" ? workspacePanel.current : payrollPanel.current;
    if (!incoming) return;
    if (changed) incoming.querySelector("nav")?.scrollTo({ top: 0 });
    if (changed && focusWasInside.current) {
      const target = incoming.querySelector<HTMLElement>(
        scope === "payroll"
          ? '[aria-label="Back to workspace"]'
          : '[data-sidebar-module="payroll"]',
      );
      target?.focus({ preventScroll: true });
      target?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "instant" });
    }
    if (!outgoing) return;
    if (
      reducedMotion ||
      typeof incoming.animate !== "function" ||
      incoming.getBoundingClientRect().width === 0
    ) {
      setPresence({ current: scope, outgoing: null });
      return;
    }
    const tokens = getComputedStyle(incoming);
    const options: KeyframeAnimationOptions = {
      duration: Number.parseFloat(tokens.getPropertyValue("--dur-base")) || 220,
      easing: tokens.getPropertyValue("--ease-out").trim() || "ease-out",
      fill: "both",
    };
    const direction = scope === "payroll" ? 1 : -1;
    return playSidebarSlide(incoming, outgoing, direction, options, () => {
      setPresence({ current: scope, outgoing: null });
    });
  }, [scope, reducedMotion]);

  return (
    <div ref={root} className="relative min-h-0 overflow-hidden">
      {(["workspace", "payroll"] as const).map((panel) =>
        scope === panel || presence.outgoing === panel ? (
          <div
            key={panel}
            ref={panel === "workspace" ? workspacePanel : payrollPanel}
            inert={panel !== scope}
            aria-hidden={panel !== scope ? true : undefined}
            className="absolute inset-0 grid min-h-0 grid-rows-[minmax(0,1fr)]"
          >
            {panel === "workspace" ? workspace : payroll}
          </div>
        ) : null,
      )}
    </div>
  );
}
